import { UserCog } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { CustomerDetail, CustomerStatus } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { CUSTOMER_STATUSES, customerStatusLabel } from './constants'
import { useChangeCustomerStatus } from './mutations'
import { useCustomerPermissions } from './permissions'

const MINIMUM_REASON_LENGTH = 5

/** The changes that shut a customer out of the institution rather than merely reclassifying them. */
const SEVERE: readonly CustomerStatus[] = ['Blacklisted', 'Deceased', 'Closed']

const CONSEQUENCES: Record<CustomerStatus, string> = {
  Active: 'The customer may transact and hold new facilities again.',
  Dormant: 'The record is parked. Reactivation is a deliberate step before it can transact.',
  Blacklisted:
    'Every branch sees the blacklist on the exposure banner, and new lending is refused across the institution.',
  Deceased: 'The record is closed to new activity, and the estate process takes over the balances.',
  Closed: 'The relationship ends. The record is retained for the statutory period and is not deleted.',
}

/**
 * Changing a customer's standing.
 *
 * The reason is not paperwork: it is what the next officer at another branch reads off the
 * exposure banner months later, and it is the only account of the decision the audit trail will
 * ever have. So it is captured here, with the decision, rather than reconstructed afterwards.
 */
export function CustomerStatusAction({ customer }: { customer: CustomerDetail }) {
  const permissions = useCustomerPermissions()
  const [open, setOpen] = useState(false)
  // The empty string rather than undefined: a select that starts undefined is uncontrolled, and
  // becomes controlled the moment someone chooses — which React and Radix both object to.
  const [status, setStatus] = useState<CustomerStatus | ''>('')
  const [reason, setReason] = useState('')

  const mutation = useChangeCustomerStatus(customer.id)

  useEffect(() => {
    if (!open) {
      setStatus('')
      setReason('')
    }
  }, [open])

  if (!permissions.canChangeStatus) {
    return null
  }

  const trimmed = reason.trim()
  const ready = status !== '' && trimmed.length >= MINIMUM_REASON_LENGTH
  const severe = status !== '' && SEVERE.includes(status)

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <UserCog />
        Change status
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change customer status</DialogTitle>
            <DialogDescription>
              {customer.fullName} is currently {customerStatusLabel(customer.status).toLowerCase()}.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="status-change-target">New status</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as CustomerStatus)}>
                <SelectTrigger id="status-change-target">
                  <SelectValue placeholder="Select a status…" />
                </SelectTrigger>
                <SelectContent>
                  {CUSTOMER_STATUSES.filter((value) => value !== customer.status).map((value) => (
                    <SelectItem key={value} value={value}>
                      {customerStatusLabel(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {status ? <p className="text-xs text-body">{CONSEQUENCES[status]}</p> : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="status-change-reason">Reason</Label>
              <Input
                id="status-change-reason"
                value={reason}
                autoComplete="off"
                onChange={(event) => setReason(event.target.value)}
                aria-describedby="status-change-reason-help"
              />
              <p id="status-change-reason-help" className="text-xs text-muted">
                Written into the audit trail, and shown to whoever reads this record next.
              </p>
            </div>

            {mutation.isError ? <ProblemAlert error={mutation.error} /> : null}
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              variant={severe ? 'danger' : 'primary'}
              disabled={!ready || mutation.isPending}
              onClick={() => {
                if (!status || !ready) {
                  return
                }

                mutation.mutate(
                  { status, reason: trimmed },
                  {
                    onSuccess: () => {
                      setOpen(false)
                    },
                  },
                )
              }}
            >
              {mutation.isPending ? 'Working…' : 'Change status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
